import clsx from 'clsx';
import { styled } from '@mui/material/styles';
import { SectionStubDto } from 'models/dtoTypes';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';

import UserNavbarHeader from 'components/UserNavbarHeader';
import Logo from 'logos/Logo';
import { drawerWidth } from 'kosmosConfig';
import SidebarItem from './_sidebarItem';
import generateMenuList from 'kosmos/kosmosUtils/generateMenuList';
import { useTranslation } from 'react-i18next';

const PREFIX = 'SmeDrawer';

const classes = {
  drawerPaper: `${PREFIX}-drawerPaper`,
  drawerPaperClose: `${PREFIX}-drawerPaperClose`,
  toolbarIcon: `${PREFIX}-toolbarIcon`,
  list: `${PREFIX}-list`,
  logo: `${PREFIX}-logo`,
  collapse: `${PREFIX}-collapse`,
};

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  position: 'fixed',
  height: '100vh',
  [`& .${classes.drawerPaper}`]: {
    backgroundColor: '#121212',
    position: 'relative',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },

  [`& .${classes.drawerPaperClose}`]: {
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9),
    },
  },

  [`& .${classes.toolbarIcon}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0 8px',
    color: 'white',
    backgroundColor: '#23c8a7',
    ...theme.mixins.toolbar,
  },

  [`& .${classes.list}`]: {
    color: 'white',
  },

  [`& .${classes.logo}`]: {
    margin: '0 auto',
  },

  [`& .${classes.collapse}`]: {
    position: 'absolute',
  },
}));

interface OnDrawerClose {
  (): void;
}

interface Props {
  open: boolean;
  onDrawerClose: OnDrawerClose;
  sections: SectionStubDto[];
  logoUrl?: string;
}

export default function SmeDrawer(props: Props): JSX.Element {
  const { open, onDrawerClose, sections, logoUrl } = props;
  const { t } = useTranslation('default');

  const menuList = generateMenuList(sections, t);
  return (
    <StyledDrawer
      variant="permanent"
      classes={{
        paper: clsx(classes.drawerPaper, !open && classes.drawerPaperClose),
      }}
      open={open}
    >
      <div className={classes.toolbarIcon}>
        <Logo
          src={logoUrl}
          className={classes.logo}
          height={75}
          color="white"
          alt="coriolisLogoWhite"
        />
        <IconButton
          className={classes.collapse}
          onClick={onDrawerClose}
          color="inherit"
          size="large"
        >
          <ChevronLeftIcon />
        </IconButton>
      </div>
      <UserNavbarHeader open={open} />

      <List dense={true} className={classes.list}>
        {menuList.map((section, i) => (
          <SidebarItem
            key={i}
            label={section.label}
            icon={section.icon}
            subItems={section.subItems}
            path={section.path}
            minimise={!open}
          />
        ))}
      </List>
    </StyledDrawer>
  );
}
